import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { LayoutGroup } from 'framer-motion';
import { PlusOutlined, DragOutlined } from '@ant-design/icons';
import { DndContext, DragOverlay, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import IssueUpgradeCard from '@/components/module/projects/issues_upgrades_tab/IssueUpgradeCard';
import ClientTicketCard from '@/components/module/projects/client_tickets_tab/ClientTicketCard';
import { getIssueOrUpgradeApiId, ISSUE_UPGRADE_COL } from '@hooks/projects/useProjectIssuesAndUpgrades';
import { getClientTicketApiId } from '@hooks/projects/useProjectClientTickets';
import {
    SprintKanbanScrollChainStyle,
    SprintKanbanDragCursorStyle,
} from '@/components/module/projects/sprints_tab/SprintKanbanBoard';

const dotWave = keyframes`
    0%, 80%, 100% { transform: translateY(0); opacity: 0.55; }
    40% { transform: translateY(-3px); opacity: 1; }
`;

export const WaveLoaderContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px 0;
    width: 100%;
    margin-top: 12px;
    animation: bounceIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;

    @keyframes bounceIn {
        0% {
            transform: scale(0.3);
            opacity: 0;
        }
        50% {
            transform: scale(1.05);
            opacity: 0.8;
        }
        70% {
            transform: scale(0.9);
            opacity: 0.9;
        }
        100% {
            transform: scale(1);
            opacity: 1;
        }
    }
`;

export const DotWaveLoader = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 16px;

    span {
        width: 6px;
        height: 6px;
        border-radius: 999px;
        background: var(--ant-color-primary, #7c3aed);
        animation: ${dotWave} 1s ease-in-out infinite;
    }

    span:nth-child(2) {
        animation-delay: 0.12s;
    }
    span:nth-child(3) {
        animation-delay: 0.24s;
    }
`;
import { useSprintKanbanSyncHorizontalScroll } from '@hooks/projects/useProjectSprints';
import {
    BoardCollapseSectionHeading,
    SprintKanbanFill,
    KanbanColumnPanel,
    KanbanColumnHeader,
    KanbanColumnTitle,
    KanbanColumnCountBadge,
    TaskCardKanbanStatusPillCount,
    KanbanColumnBody,
    KanbanDragDropPlaceholder,
    KANBAN_DESKTOP_COLUMN_MIN_PX,
    kanbanDesktopHeaderCellFlexStyle,
    kanbanDesktopBoardRowInnerStyle,
    kanbanDesktopBoardHeaderRowInnerStyle,
    kanbanDesktopFixedHeaderCellStyle,
    kanbanDesktopFixedBoardRowInnerStyle,
    kanbanDesktopFixedBoardHeaderRowInnerStyle,
} from '@/pages/inside/tenant/style';

const COL_VARIANT = { ACTIVE: 'todo', SUBMITTED: 'bugs', RESOLVED: 'completed' };
const FIXED_COLUMN_STYLE = {
    flex: `0 0 ${KANBAN_DESKTOP_COLUMN_MIN_PX}px`,
    width: KANBAN_DESKTOP_COLUMN_MIN_PX,
    minWidth: KANBAN_DESKTOP_COLUMN_MIN_PX,
    maxWidth: KANBAN_DESKTOP_COLUMN_MIN_PX,
};
const ADAPTIVE_COLUMN_STYLE = {
    flex: '1 1 0%',
    width: 0,
    minWidth: KANBAN_DESKTOP_COLUMN_MIN_PX,
    maxWidth: 'none',
};

function EmptyColumn({ title }) {
    return (
        <div
            style={{
                width: '100%',
                minHeight: 120,
                borderRadius: 12,
                border: '1px dashed var(--ant-color-border-secondary, #d9d9d9)',
                background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                textAlign: 'center',
                color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))',
                fontSize: 13,
            }}
        >
            No items in {title}
        </div>
    );
}

/** Module scope: stable component identity so parent re-renders (e.g. modals) do not remount columns and reset scroll. */
function DroppableKanbanColumnPanel({ columnId, isFixedColumnMode, children, ...panelProps }) {
    const { setNodeRef } = useDroppable({ id: columnId });
    return (
        <KanbanColumnPanel
            ref={setNodeRef}
            $fixedDesktop={isFixedColumnMode}
            style={isFixedColumnMode ? FIXED_COLUMN_STYLE : ADAPTIVE_COLUMN_STYLE}
            {...panelProps}
        >
            {children}
        </KanbanColumnPanel>
    );
}

function DraggableIssueCard({
    item,
    sourceColumn,
    boardKind,
    resolvedColumnId,
    nonDraggableSet,
    canDrag,
    children,
}) {
    const id = boardKind === 'client_ticket' ? getClientTicketApiId(item) : getIssueOrUpgradeApiId(item);
    const dragDisabledFromResolved = sourceColumn === resolvedColumnId;
    const dragDisabledFromCustom = nonDraggableSet.has(String(sourceColumn));
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: `${boardKind === 'client_ticket' ? 'client-ticket' : 'issue-upgrade'}-${String(id)}`,
        data: { id, sourceColumn, item },
        disabled: !canDrag || !id || dragDisabledFromResolved || dragDisabledFromCustom,
    });
    const style = transform
        ? {
              transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
          }
        : undefined;
    return (
        <div ref={setNodeRef} style={{ ...style, opacity: isDragging ? 0.55 : 1, width: '100%' }} {...attributes} {...listeners}>
            {children}
        </div>
    );
}

export default function IssuesUpgradesKanbanBoard({
    activeRows,
    submittedRows,
    resolvedRows,
    extraRows,
    canDrag,
    dropTarget,
    draggingColumn,
    onDndDragStart,
    onDndDragOverColumn,
    onDndDragEnd,
    onAdd,
    onSelectItem,
    canSubmit = false,
    canResolve = false,
    canUpdate = false,
    canDelete = false,
    currentUserId = null,
    onEditItem,
    onDeleteItem,
    canCreateProjectTask = false,
    onOpenCreateProjectTask,
    deletePending = false,
    boardTitle = 'Issues & Upgrades',
    activeLabel = 'Active',
    submittedLabel = 'Submitted',
    resolvedLabel = 'Resolve',
    extraLabel = '',
    showHeader = true,
    columnMode = 'adaptive',
    extraColumnId = 'client-submitted',
    nonDraggableColumnIds = [],
    canDropRule,
    boardKind = 'issue_upgrade',
    activeColumnId = ISSUE_UPGRADE_COL.ACTIVE,
    submittedColumnId = ISSUE_UPGRADE_COL.SUBMITTED,
    resolvedColumnId = ISSUE_UPGRADE_COL.RESOLVED,
    getDropBlockedMessage,
    hasMore = false,
    onLoadMore = undefined,
    isLoadingMore = false,
}) {
    const { headerScrollRef, bodyScrollRef, onHeaderScroll, onBodyScroll } = useSprintKanbanSyncHorizontalScroll();
    
    const [scrolledColumnId, setScrolledColumnId] = useState(null);

    useEffect(() => {
        if (!isLoadingMore) {
            setScrolledColumnId(null);
        }
    }, [isLoadingMore]);

    const handleScroll = useCallback(
        (event, columnId) => {
            if (!hasMore || !onLoadMore || isLoadingMore) return;
            const target = event.currentTarget;
            if (target.scrollHeight - target.scrollTop <= target.clientHeight + 24) {
                setScrolledColumnId(columnId);
                onLoadMore();
            }
        },
        [hasMore, onLoadMore, isLoadingMore]
    );

    const hasExtraColumn = Array.isArray(extraRows);
    const isFixedColumnMode = columnMode === 'fixed';
    const total =
        (activeRows?.length || 0) +
        (submittedRows?.length || 0) +
        (resolvedRows?.length || 0) +
        (hasExtraColumn ? extraRows.length || 0 : 0);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
    const [activeDragPayload, setActiveDragPayload] = useState(null);
    const nonDraggableSet = useMemo(
        () => new Set((Array.isArray(nonDraggableColumnIds) ? nonDraggableColumnIds : []).map((x) => String(x))),
        [nonDraggableColumnIds],
    );

    const handleDragStart = useCallback(
        (event) => {
            const payload = event?.active?.data?.current ?? null;
            setActiveDragPayload(payload);
            onDndDragStart?.(payload);
        },
        [onDndDragStart],
    );
    const handleDragOver = useCallback(
        (event) => {
            const overColumnId = event?.over?.id ?? null;
            const payload = event?.active?.data?.current ?? null;
            if (!overColumnId) return;
            const col = String(overColumnId);
            const sourceColumn = payload?.sourceColumn ?? null;
            const canDrop = canDropRule
                ? canDropRule(sourceColumn, col)
                : (col === submittedColumnId && canSubmit) ||
                  (col === resolvedColumnId && canResolve) ||
                  (col === activeColumnId && canUpdate);
            if (canDrop) onDndDragOverColumn?.(col, payload);
        },
        [
            onDndDragOverColumn,
            canSubmit,
            canResolve,
            canUpdate,
            canDropRule,
            submittedColumnId,
            resolvedColumnId,
            activeColumnId,
        ],
    );
    const handleDragEnd = useCallback(
        (event) => {
            const overColumnId = event?.over?.id ?? null;
            const payload = event?.active?.data?.current ?? null;
            const col = overColumnId != null ? String(overColumnId) : null;
            const sourceColumn = payload?.sourceColumn ?? null;
            const canDrop = canDropRule
                ? canDropRule(sourceColumn, col)
                : (col === submittedColumnId && canSubmit) ||
                  (col === resolvedColumnId && canResolve) ||
                  (col === activeColumnId && canUpdate);
            onDndDragEnd?.({ overColumnId: canDrop ? col : null, payload });
            setActiveDragPayload(null);
        },
        [
            onDndDragEnd,
            canSubmit,
            canResolve,
            canUpdate,
            canDropRule,
            submittedColumnId,
            resolvedColumnId,
            activeColumnId,
        ],
    );
    const handleDragCancel = useCallback(() => setActiveDragPayload(null), []);
    const handleBoardWheelCapture = useCallback(
        (event) => {
            if (!isFixedColumnMode) return;
            const bodyEl = bodyScrollRef?.current;
            if (!bodyEl) return;
            const deltaX = Number(event?.deltaX ?? 0);
            if (Math.abs(deltaX) < 0.5) return;
            const hasHorizontalOverflow = bodyEl.scrollWidth > bodyEl.clientWidth + 1;
            if (hasHorizontalOverflow) {
                bodyEl.scrollLeft += deltaX;
            }
            // Prevent browser back/forward trackpad navigation while swiping over horizontal kanban.
            event.preventDefault();
            event.stopPropagation();
        },
        [bodyScrollRef, isFixedColumnMode],
    );
    useEffect(() => {
        if (!isFixedColumnMode) return undefined;
        const bodyEl = bodyScrollRef?.current;
        if (!bodyEl) return undefined;
        const onNativeWheel = (event) => {
            const deltaX = Number(event?.deltaX ?? 0);
            if (Math.abs(deltaX) < 0.5) return;
            const hasHorizontalOverflow = bodyEl.scrollWidth > bodyEl.clientWidth + 1;
            if (hasHorizontalOverflow) {
                bodyEl.scrollLeft += deltaX;
            }
            // Force-stop browser history swipe navigation on horizontal gesture.
            event.preventDefault();
            event.stopPropagation();
        };
        bodyEl.addEventListener('wheel', onNativeWheel, { passive: false });
        return () => {
            bodyEl.removeEventListener('wheel', onNativeWheel);
        };
    }, [bodyScrollRef, isFixedColumnMode]);

    const canDropOnColumn = useCallback(
        (columnId) => {
            if (!canDrag || !draggingColumn || draggingColumn === columnId) return false;
            if (canDropRule) return !!canDropRule(draggingColumn, columnId);
            if (columnId === submittedColumnId) return !!canSubmit;
            if (columnId === resolvedColumnId) return !!canResolve;
            if (columnId === activeColumnId) return !!canUpdate;
            if (columnId === extraColumnId) return false;
            return false;
        },
        [
            canDrag,
            draggingColumn,
            canSubmit,
            canResolve,
            canUpdate,
            canDropRule,
            extraColumnId,
            submittedColumnId,
            resolvedColumnId,
            activeColumnId,
        ],
    );

    const showDragPlaceholder = (columnId) => Boolean(canDropOnColumn(columnId));
    const dropZoneActive = (columnId) => Boolean(canDropOnColumn(columnId) && dropTarget === columnId);

    const itemOwnershipMatch = useCallback(
        (row) => {
            const createdById =
                row?.createdBy?.id ??
                row?.createdById ??
                row?.created_by ??
                row?.created_by_id ??
                row?.createdBy;
            return String(createdById ?? '') === String(currentUserId ?? '');
        },
        [currentUserId],
    );

    const renderCard = (item, sourceColumn, idx) => {
        const id =
            (boardKind === 'client_ticket' ? getClientTicketApiId(item) : getIssueOrUpgradeApiId(item)) ??
            `${sourceColumn}-${idx}`;
        const owns = itemOwnershipMatch(item);
        const allowEdit = Boolean(canUpdate && owns);
        const allowDelete = Boolean(canDelete && owns);
        return (
            <div key={id} style={{ width: '100%' }}>
                <DraggableIssueCard
                    item={item}
                    sourceColumn={sourceColumn}
                    boardKind={boardKind}
                    resolvedColumnId={resolvedColumnId}
                    nonDraggableSet={nonDraggableSet}
                    canDrag={canDrag}
                >
                    {boardKind === 'client_ticket' ? (
                        <ClientTicketCard item={item} onSelect={onSelectItem ? () => onSelectItem(item) : undefined} />
                    ) : (
                        <IssueUpgradeCard
                            item={item}
                            onSelect={onSelectItem ? () => onSelectItem(item) : undefined}
                            canEdit={allowEdit}
                            canDelete={allowDelete}
                            onEditClick={allowEdit && typeof onEditItem === 'function' ? onEditItem : undefined}
                            onDeleteClick={allowDelete && typeof onDeleteItem === 'function' ? onDeleteItem : undefined}
                            canCreateProjectTask={Boolean(canCreateProjectTask)}
                            onCreateProjectTaskClick={
                                typeof onOpenCreateProjectTask === 'function' ? onOpenCreateProjectTask : undefined
                            }
                            deletePending={deletePending}
                            showKanbanOverflowMenu={String(sourceColumn) === String(activeColumnId)}
                        />
                    )}
                </DraggableIssueCard>
            </div>
        );
    };

    const renderColumnBody = (columnId, list, title) => (
        <KanbanColumnBody
            className="sprint-kanban-column-body-natural"
            $qaStack
            data-drag-placeholder={showDragPlaceholder(columnId) ? 'true' : undefined}
            $dragPlaceholder={showDragPlaceholder(columnId)}
            onScroll={(e) => handleScroll(e, columnId)}
        >
            {draggingColumn && draggingColumn !== columnId && !canDropOnColumn(columnId) ? (
                <KanbanDragDropPlaceholder role="status" $active={false}>
                    <DragOutlined style={{ fontSize: 24, opacity: 0.5 }} aria-hidden />
                    <span>
                        {canDropRule
                            ? (() => {
                                  const hint =
                                      typeof getDropBlockedMessage === 'function' && draggingColumn != null
                                          ? getDropBlockedMessage(String(draggingColumn), String(columnId))
                                          : null;
                                  if (hint && String(hint).trim()) return hint;
                                  return "Can't drop on this column.";
                                })()
                            : columnId === submittedColumnId
                              ? "No 'submit' permission"
                              : columnId === resolvedColumnId
                                ? "No 'resolve' permission"
                                : columnId === activeColumnId
                                  ? "No 'update' permission"
                                  : 'No permission'}
                    </span>
                </KanbanDragDropPlaceholder>
            ) : showDragPlaceholder(columnId) ? (
                <KanbanDragDropPlaceholder role="status" $active={dropZoneActive(columnId)}>
                    <DragOutlined style={{ fontSize: 24, opacity: 0.88 }} aria-hidden />
                    <span>
                        {columnId === activeColumnId
                            ? `Drop here to move to ${activeLabel}`
                            : columnId === submittedColumnId
                                ? `Drop here to move to ${submittedLabel}`
                                : `Drop here to move to ${resolvedLabel}`}
                    </span>
                </KanbanDragDropPlaceholder>
            ) : (
                <>
                    {list.length === 0 ? (
                        <EmptyColumn title={title} />
                    ) : (
                        list.map((item, idx) => renderCard(item, columnId, idx))
                    )}
                    {hasMore && isLoadingMore && scrolledColumnId === columnId && (
                        <WaveLoaderContainer>
                            <DotWaveLoader>
                                <span />
                                <span />
                                <span />
                            </DotWaveLoader>
                        </WaveLoaderContainer>
                    )}
                </>
            )}
        </KanbanColumnBody>
    );

    return (
        <Container className="sprint-kanban-root" $display="flex" $flexDirection="column" $flex="1" $minHeight="0" $overflow="hidden">
            <SprintKanbanDragCursorStyle />
            <SprintKanbanScrollChainStyle />
            {(showHeader || typeof onAdd === 'function') && (
                <Container
                    $display="flex"
                    $justifyContent="space-between"
                    $alignItems="center"
                    $gap="12px"
                    style={{ marginBottom: 16 }}
                >
                    {showHeader ? (
                        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
                            <BoardCollapseSectionHeading>{boardTitle}</BoardCollapseSectionHeading>
                            <span style={{ fontSize: 18, fontWeight: 600 }}>{total}</span>
                        </span>
                    ) : (
                        <span />
                    )}
                    {typeof onAdd === 'function' ? (
                        <Button
                            type="button"
                            $primary
                            $blackOnLight
                            $size="13px"
                            $padding="6px 14px"
                            icon={<PlusOutlined />}
                            onClick={onAdd}
                        >
                            Create
                        </Button>
                    ) : null}
                </Container>
            )}

            <DndContext sensors={sensors} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
                <SprintKanbanFill className="sprint-kanban-fill-natural">
                    <LayoutGroup id="issues-upgrades-kanban-board">
                        <div className="sprint-kanban-desktop-split">
                            <div data-sprint-kanban-sticky-strip className="sprint-kanban-sticky-strip">
                                <div
                                    ref={headerScrollRef}
                                    className="sprint-kanban-h-scroll-sync"
                                    onScroll={onHeaderScroll}
                                >
                                    <div
                                        style={{
                                            ...(isFixedColumnMode
                                                ? kanbanDesktopFixedBoardHeaderRowInnerStyle
                                                : kanbanDesktopBoardHeaderRowInnerStyle),
                                            paddingLeft: isFixedColumnMode ? 24 : 0,
                                            paddingRight: isFixedColumnMode ? 24 : 0,
                                            boxSizing: 'border-box',
                                        }}
                                    >
                                        <div style={isFixedColumnMode ? kanbanDesktopFixedHeaderCellStyle : kanbanDesktopHeaderCellFlexStyle}>
                                            <KanbanColumnHeader>
                                                <KanbanColumnTitle>{activeLabel}</KanbanColumnTitle>
                                                <KanbanColumnCountBadge $variant={COL_VARIANT.ACTIVE}>{activeRows.length}</KanbanColumnCountBadge>
                                            </KanbanColumnHeader>
                                        </div>
                                        <div style={isFixedColumnMode ? kanbanDesktopFixedHeaderCellStyle : kanbanDesktopHeaderCellFlexStyle}>
                                            <KanbanColumnHeader>
                                                <KanbanColumnTitle>{submittedLabel}</KanbanColumnTitle>
                                                <KanbanColumnCountBadge $variant={COL_VARIANT.SUBMITTED}>{submittedRows.length}</KanbanColumnCountBadge>
                                            </KanbanColumnHeader>
                                        </div>
                                        <div style={isFixedColumnMode ? kanbanDesktopFixedHeaderCellStyle : kanbanDesktopHeaderCellFlexStyle}>
                                            <KanbanColumnHeader>
                                                <KanbanColumnTitle>{resolvedLabel}</KanbanColumnTitle>
                                                <TaskCardKanbanStatusPillCount as="span">{resolvedRows.length}</TaskCardKanbanStatusPillCount>
                                            </KanbanColumnHeader>
                                        </div>
                                        {hasExtraColumn ? (
                                            <div style={isFixedColumnMode ? kanbanDesktopFixedHeaderCellStyle : kanbanDesktopHeaderCellFlexStyle}>
                                                <KanbanColumnHeader>
                                                    <KanbanColumnTitle>{extraLabel || 'Extra'}</KanbanColumnTitle>
                                                    <TaskCardKanbanStatusPillCount as="span">{extraRows.length}</TaskCardKanbanStatusPillCount>
                                                </KanbanColumnHeader>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                            <div
                                className="sprint-kanban-desktop-body-wrap"
                                onWheelCapture={handleBoardWheelCapture}
                                style={{ overscrollBehaviorX: isFixedColumnMode ? 'none' : 'auto' }}
                            >
                                <div
                                    ref={bodyScrollRef}
                                    className="sprint-kanban-h-scroll-sync"
                                    onScroll={onBodyScroll}
                                    style={{ overscrollBehaviorX: isFixedColumnMode ? 'none' : 'auto' }}
                                >
                                    <div
                                        className="sprint-kanban-desktop-board-row"
                                        style={{
                                            ...(isFixedColumnMode ? kanbanDesktopFixedBoardRowInnerStyle : kanbanDesktopBoardRowInnerStyle),
                                            paddingLeft: isFixedColumnMode ? 24 : 0,
                                            paddingRight: isFixedColumnMode ? 24 : 0,
                                            boxSizing: 'border-box',
                                        }}
                                    >
                                        <DroppableKanbanColumnPanel columnId={activeColumnId} isFixedColumnMode={isFixedColumnMode}>
                                            {renderColumnBody(activeColumnId, activeRows, activeLabel)}
                                        </DroppableKanbanColumnPanel>
                                        <DroppableKanbanColumnPanel columnId={submittedColumnId} isFixedColumnMode={isFixedColumnMode}>
                                            {renderColumnBody(submittedColumnId, submittedRows, submittedLabel)}
                                        </DroppableKanbanColumnPanel>
                                        <DroppableKanbanColumnPanel columnId={resolvedColumnId} isFixedColumnMode={isFixedColumnMode}>
                                            {renderColumnBody(resolvedColumnId, resolvedRows, resolvedLabel)}
                                        </DroppableKanbanColumnPanel>
                                        {hasExtraColumn ? (
                                            <KanbanColumnPanel
                                                $fixedDesktop={isFixedColumnMode}
                                                style={isFixedColumnMode ? FIXED_COLUMN_STYLE : ADAPTIVE_COLUMN_STYLE}
                                            >
                                                {renderColumnBody(extraColumnId, extraRows, extraLabel || 'Extra')}
                                            </KanbanColumnPanel>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </LayoutGroup>
                </SprintKanbanFill>
                <DragOverlay>
                    {activeDragPayload?.id ? (
                        <div style={{ width: 420, maxWidth: '92vw' }}>
                            {boardKind === 'client_ticket' ? (
                                <ClientTicketCard
                                    item={
                                        [...(activeRows ?? []), ...(submittedRows ?? []), ...(resolvedRows ?? [])].find(
                                            (x) => String(getClientTicketApiId(x) ?? '') === String(activeDragPayload.id),
                                        ) ?? null
                                    }
                                />
                            ) : (
                                <IssueUpgradeCard
                                    suppressCardMenu
                                    item={
                                        [...(activeRows ?? []), ...(submittedRows ?? []), ...(resolvedRows ?? [])].find(
                                            (x) => String(getIssueOrUpgradeApiId(x) ?? '') === String(activeDragPayload.id),
                                        ) ?? null
                                    }
                                />
                            )}
                        </div>
                    ) : null}
                </DragOverlay>
            </DndContext>
        </Container>
    );
}
